// /sign-in-session?session=<id> — the explicit Continue for a CLI / plugin
// sign-in link (server CORE-AUTH-TAKEOVER-PATHS D9). The shell sends a
// signed-in visitor of `/login?session=<id>` here (functions/auth.js
// authRedirectTarget). Nothing is armed on load: the page reads the session
// facts (loadSignInSession) and arms it only on the Continue click
// (confirmSignInSession), with the device's code when the server asks for it.
export const signInSession = {
  extends: 'Page',
  metadata: (el) => ({ title: el.call('polyglot', 'workspace.signInSession.page.title') }),
  flow: 'y',
  align: 'center center',
  padding: 'D C',
  width: '100%',
  minHeight: '100%',

  state: {
    requested: false,
    loading: true,
    error: null,
    sessionId: '',
    createdAt: '',
    requesterIp: '',
    client: '',
    codeRequired: false,
    code: '',
    codeError: null,
    submitting: false,
    done: false
  },

  onRender: (el) => {
    el.call('loadSignInSession')
  },

  SessionCard: {
    flow: 'y',
    gap: 'B',
    padding: 'D',
    width: '100%',
    maxWidth: 'H',
    round: 'radiusSheet',
    background: 'overlay',
    borderWidth: 'hairline',
    borderStyle: 'solid',
    borderColor: 'line',

    H1: {
      fontSize: 'C',
      fontWeight: '700',
      color: 'title',
      margin: '0',
      text: '{{ workspace.signInSession.title | polyglot }}'
    },

    LoadingSpinner: {
      if: (el, s) => !!s.loading,
      boxSize: 'C'
    },

    ErrorText: {
      if: (el, s) => !s.loading && !!s.error,
      role: 'alert',
      color: 'paragraph',
      text: (el, s) => el.call('polyglot', s.error)
    },

    DoneText: {
      if: (el, s) => !!s.done,
      role: 'status',
      color: 'paragraph',
      text: '{{ workspace.signInSession.done | polyglot }}'
    },

    Ask: {
      if: (el, s) => !s.loading && !s.error && !s.done,
      flow: 'y',
      gap: 'B',

      Lead: {
        tag: 'p',
        margin: '0',
        color: 'paragraph',
        text: '{{ workspace.signInSession.lead | polyglot }}'
      },

      Facts: {
        tag: 'dl',
        flow: 'y',
        gap: 'Z',
        margin: '0',
        StartedRow: {
          flow: 'x',
          gap: 'A',
          Dt: { tag: 'dt', color: 'caption', text: '{{ workspace.signInSession.started | polyglot }}' },
          Dd: {
            tag: 'dd',
            margin: '0',
            color: 'title',
            text: (el, s) =>
              s.createdAt
                ? el.call('localizedDate', s.createdAt, {
                    day: 'numeric',
                    month: 'short',
                    hour: 'numeric',
                    minute: '2-digit'
                  })
                : '—'
          }
        },
        FromRow: {
          flow: 'x',
          gap: 'A',
          Dt: { tag: 'dt', color: 'caption', text: '{{ workspace.signInSession.from | polyglot }}' },
          Dd: { tag: 'dd', margin: '0', color: 'title', text: (el, s) => s.requesterIp || '—' }
        },
        ClientRow: {
          flow: 'x',
          gap: 'A',
          Dt: { tag: 'dt', color: 'caption', text: '{{ workspace.signInSession.client | polyglot }}' },
          Dd: {
            tag: 'dd',
            margin: '0',
            color: 'title',
            text: (el, s) =>
              s.client === 'cli'
                ? el.call('polyglot', 'workspace.signInSession.clientCli')
                : s.client || el.call('polyglot', 'workspace.signInSession.clientUnknown')
          }
        }
      },

      Warning: {
        tag: 'p',
        margin: '0',
        color: 'warningInk',
        text: '{{ workspace.signInSession.warning | polyglot }}'
      },

      CodeField: {
        if: (el, s) => !!s.codeRequired,
        flow: 'y',
        gap: 'Z',
        Label: {
          tag: 'label',
          color: 'caption',
          text: '{{ workspace.signInSession.codeLabel | polyglot }}'
        },
        Input: {
          name: 'user_code',
          autocomplete: 'off',
          spellcheck: 'false',
          maxlength: '12',
          placeholder: 'XXXX-XXXX',
          'aria-label': (el) => el.call('polyglot', 'workspace.signInSession.codeLabel'),
          value: (el, s) => s.code,
          onInput: (ev, el, s) => {
            s.update({ code: ev.target.value, codeError: null })
          }
        },
        CodeError: {
          if: (el, s) => !!s.codeError,
          role: 'alert',
          color: 'warningInk',
          text: (el, s) => el.call('polyglot', s.codeError)
        }
      },

      Actions: {
        flow: 'x',
        gap: 'A',
        ContinueButton: {
          extends: ['Button', 'ClickableItem'],
          text: '{{ workspace.signInSession.continue | polyglot }}',
          disabled: (el, s) => !!s.submitting,
          onClick: async (ev, el) => {
            await el.call('confirmSignInSession')
          }
        },
        CancelButton: {
          extends: ['Button', 'ClickableItem'],
          theme: 'transparent',
          text: '{{ workspace.signInSession.cancel | polyglot }}',
          onClick: async (ev, el) => {
            await el.router('/', el.getRoot())
          }
        }
      }
    }
  }
}
